import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./lib/auth/AuthContext";
import { PageShell } from "./components/Layout/PageShell";
import { Spinner } from "./components/ui/spinner";
import {
  ADMIN_ROUTE_PATHS,
  SIGNUP_ROUTE_PATH,
  DISPLAY_ROUTE_PATH,
  PRIVACY_ROUTE_PATH,
  TERMS_ROUTE_PATH,
} from "./lib/urls";

// Route modules are deferred so each public or authenticated entry downloads only its own surface;
// static imports would force every mobile visitor to parse the complete application.
const OwnerPage = lazy(() => import("./routes/OwnerPage").then(({ OwnerPage }) => ({ default: OwnerPage })));
const JoinPage = lazy(() => import("./routes/JoinPage").then(({ JoinPage }) => ({ default: JoinPage })));
const JoinDisplayPage = lazy(() =>
  import("./routes/JoinDisplayPage").then(({ JoinDisplayPage }) => ({ default: JoinDisplayPage })),
);
const VerifyEmailPage = lazy(() =>
  import("./routes/VerifyEmailPage").then(({ VerifyEmailPage }) => ({ default: VerifyEmailPage })),
);
const PreferencesPage = lazy(() =>
  import("./routes/PreferencesPage").then(({ PreferencesPage }) => ({ default: PreferencesPage })),
);
const CampaignUnsubscribePage = lazy(() =>
  import("./routes/CampaignUnsubscribePage").then(({ CampaignUnsubscribePage }) => ({
    default: CampaignUnsubscribePage,
  })),
);
const PrivacyPolicyPage = lazy(() =>
  import("./routes/legal/PrivacyPolicyPage").then(({ PrivacyPolicyPage }) => ({ default: PrivacyPolicyPage })),
);
const TermsPage = lazy(() =>
  import("./routes/legal/TermsPage").then(({ TermsPage }) => ({ default: TermsPage })),
);
const NotFoundPage = lazy(() =>
  import("./routes/NotFoundPage").then(({ NotFoundPage }) => ({ default: NotFoundPage })),
);

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense
          fallback={
            <PageShell showFooter={false}>
              <Spinner label="Loading page" />
            </PageShell>
          }
        >
          <Routes>
            <Route path="/" element={<OwnerPage />} />
            <Route path={SIGNUP_ROUTE_PATH} element={<JoinPage />} />
            <Route path={DISPLAY_ROUTE_PATH} element={<JoinDisplayPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/preferences" element={<PreferencesPage />} />
            <Route path="/campaign-unsubscribe" element={<CampaignUnsubscribePage />} />
            <Route path={PRIVACY_ROUTE_PATH} element={<PrivacyPolicyPage />} />
            <Route path={TERMS_ROUTE_PATH} element={<TermsPage />} />
            <Route path="/owner" element={<OwnerPage />} />
            {/* The administrator's own scopes. They resolve to the same authenticated entry the
              * owner uses — one sign-in, one shell — but the path, not a query parameter, states
              * whether the platform or a single business is being operated on. */}
            {ADMIN_ROUTE_PATHS.map((path) => (
              <Route key={path} path={path} element={<OwnerPage />} />
            ))}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
